'use client';

import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Button, Switch } from '../../components/ui';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { ShopControllerGetShopItemsParams1SortByEnum } from '../../services/generated';
import { FaChevronDown } from '../../components/icons';
import { cn } from '../../utils/cn';
import translations from './ShopScreen.translations';

export interface FilterValues {
  category: string;
  weapon: string;
  type: string;
  wear: string;
  rarity: string;
  minPrice: string;
  maxPrice: string;
  statTrak: boolean;
  sortBy: string;
}

export const DEFAULT_FILTERS: FilterValues = {
  category: '',
  weapon: '',
  type: '',
  wear: '',
  rarity: '',
  minPrice: '25000',
  maxPrice: '',
  statTrak: false,
  sortBy: ShopControllerGetShopItemsParams1SortByEnum.VolumeDesc,
};

// ── Compact dropdown ──
interface MiniSelectProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

const MiniSelect: FC<MiniSelectProps> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedLabel = options.find(o => o.value === value)?.label || options[0]?.label || '';

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        className="flex items-center justify-between w-full px-3.5 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-base text-white/80 cursor-pointer transition-colors hover:bg-white/[0.07] outline-none text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="truncate">{selectedLabel}</span>
        <FaChevronDown className={cn('text-[11px] text-white/30 shrink-0 ml-2 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#0c0f16] border border-white/[0.08] rounded-xl overflow-hidden z-50 shadow-[0_8px_24px_rgba(0,0,0,0.6)] max-h-[260px] overflow-y-auto [scrollbar-width:none]">
          {options.map(opt => (
            <button
              key={opt.value}
              className={cn(
                'flex items-center w-full px-3.5 py-2.5 text-base border-none cursor-pointer transition-colors text-left',
                opt.value === value ? 'bg-white/[0.08] text-white' : 'bg-transparent text-white/50 hover:bg-white/[0.04] hover:text-white/80',
              )}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Compact input ──
const MiniInput: FC<{ value: string; placeholder: string; onChange: (v: string) => void; ariaLabel?: string }> = ({ value, placeholder, onChange, ariaLabel }) => (
  <input
    type="number"
    min="0"
    value={value}
    placeholder={placeholder}
    aria-label={ariaLabel ?? placeholder}
    onChange={e => onChange(e.target.value)}
    className="w-full px-3.5 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-base text-white/80 outline-none transition-colors focus:border-white/20 placeholder:text-white/45 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
  />
);

// ── Data ──
const CATEGORIES = [
  { value: '', labelKey: 'categoriesAll' as const },
  { value: 'Rifles', labelKey: 'categoriesRifles' as const },
  { value: 'Pistols', labelKey: 'categoriesPistols' as const },
  { value: 'SMGs', labelKey: 'categoriesSmgs' as const },
  { value: 'Heavy', labelKey: 'categoriesHeavy' as const },
  { value: 'Knives', labelKey: 'categoriesKnives' as const },
  { value: 'Gloves', labelKey: 'categoriesGloves' as const },
];

const WEAPONS_BY_CATEGORY: Record<string, Array<{ value: string; label: string }>> = {
  Rifles: [
    { value: '', label: 'All' }, { value: 'AK-47', label: 'AK-47' }, { value: 'M4A4', label: 'M4A4' },
    { value: 'M4A1-S', label: 'M4A1-S' }, { value: 'AWP', label: 'AWP' }, { value: 'AUG', label: 'AUG' },
    { value: 'SG 553', label: 'SG 553' }, { value: 'FAMAS', label: 'FAMAS' }, { value: 'Galil AR', label: 'Galil AR' },
  ],
  Pistols: [
    { value: '', label: 'All' }, { value: 'Desert Eagle', label: 'Desert Eagle' }, { value: 'Glock-18', label: 'Glock-18' },
    { value: 'USP-S', label: 'USP-S' }, { value: 'P250', label: 'P250' }, { value: 'Five-SeveN', label: 'Five-SeveN' },
    { value: 'Tec-9', label: 'Tec-9' }, { value: 'CZ75-Auto', label: 'CZ75-Auto' },
    { value: 'Dual Berettas', label: 'Dual Berettas' }, { value: 'P2000', label: 'P2000' },
  ],
  SMGs: [
    { value: '', label: 'All' }, { value: 'MP9', label: 'MP9' }, { value: 'MAC-10', label: 'MAC-10' },
    { value: 'MP7', label: 'MP7' }, { value: 'UMP-45', label: 'UMP-45' }, { value: 'P90', label: 'P90' },
    { value: 'PP-Bizon', label: 'PP-Bizon' }, { value: 'MP5-SD', label: 'MP5-SD' },
  ],
  Heavy: [
    { value: '', label: 'All' }, { value: 'Nova', label: 'Nova' }, { value: 'XM1014', label: 'XM1014' },
    { value: 'MAG-7', label: 'MAG-7' }, { value: 'Sawed-Off', label: 'Sawed-Off' },
    { value: 'M249', label: 'M249' }, { value: 'Negev', label: 'Negev' },
  ],
};

const ITEM_TYPES = [
  { value: '', labelKey: 'typesAll' as const }, { value: 'skin', labelKey: 'typesSkin' as const },
  { value: 'crate', labelKey: 'typesCrate' as const }, { value: 'keychain', labelKey: 'typesKeychain' as const },
  { value: 'graffiti', labelKey: 'typesGraffiti' as const }, { value: 'sticker', labelKey: 'typesSticker' as const },
  { value: 'agent', labelKey: 'typesAgent' as const },
];

const WEAR_CONDITIONS = [
  { value: '', labelKey: 'wearAll' as const }, { value: 'Factory New', labelKey: 'wearFactoryNew' as const },
  { value: 'Minimal Wear', labelKey: 'wearMinimalWear' as const }, { value: 'Field-Tested', labelKey: 'wearFieldTested' as const },
  { value: 'Well-Worn', labelKey: 'wearWellWorn' as const }, { value: 'Battle-Scarred', labelKey: 'wearBattleScarred' as const },
];

const RARITIES_BY_TYPE: Record<string, Array<{ value: string; label: string }>> = {
  '': [
    { value: '', label: 'All' }, { value: 'Consumer Grade', label: 'Consumer' }, { value: 'Industrial Grade', label: 'Industrial' },
    { value: 'Mil-Spec Grade', label: 'Mil-Spec' }, { value: 'Restricted', label: 'Restricted' },
    { value: 'Classified', label: 'Classified' }, { value: 'Covert', label: 'Covert' },
    { value: 'Exotic', label: 'Exotic' }, { value: 'Remarkable', label: 'Remarkable' },
    { value: 'Extraordinary', label: 'Extraordinary' }, { value: 'High Grade', label: 'High Grade' },
    { value: 'Base Grade', label: 'Base Grade' }, { value: 'Distinguished', label: 'Distinguished' },
    { value: 'Exceptional', label: 'Exceptional' }, { value: 'Superior', label: 'Superior' }, { value: 'Master', label: 'Master' },
  ],
  skin: [
    { value: '', label: 'All' }, { value: 'Consumer Grade', label: 'Consumer' }, { value: 'Industrial Grade', label: 'Industrial' },
    { value: 'Mil-Spec Grade', label: 'Mil-Spec' }, { value: 'Restricted', label: 'Restricted' },
    { value: 'Classified', label: 'Classified' }, { value: 'Covert', label: 'Covert' }, { value: 'Extraordinary', label: 'Extraordinary' },
  ],
  crate: [{ value: '', label: 'All' }, { value: 'Base Grade', label: 'Base Grade' }],
  sticker: [{ value: '', label: 'All' }, { value: 'High Grade', label: 'High Grade' }, { value: 'Remarkable', label: 'Remarkable' }, { value: 'Exotic', label: 'Exotic' }, { value: 'Extraordinary', label: 'Extraordinary' }],
  keychain: [{ value: '', label: 'All' }, { value: 'Remarkable', label: 'Remarkable' }, { value: 'Exotic', label: 'Exotic' }, { value: 'Extraordinary', label: 'Extraordinary' }],
  agent: [{ value: '', label: 'All' }, { value: 'Distinguished', label: 'Distinguished' }, { value: 'Exceptional', label: 'Exceptional' }, { value: 'Superior', label: 'Superior' }, { value: 'Master', label: 'Master' }],
  graffiti: [{ value: '', label: 'All' }, { value: 'High Grade', label: 'High Grade' }],
};

// ── Component ──
interface ShopFiltersPanelProps {
  filters: FilterValues;
  onApply: (filters: FilterValues) => void;
  onReset?: () => void;
}

export const ShopFiltersPanel: FC<ShopFiltersPanelProps> = ({ filters, onApply, onReset }) => {
  const { t } = useLocalTranslation(translations);
  const [local, setLocal] = useState<FilterValues>(filters);

  useEffect(() => { setLocal(filters); }, [filters]);

  const handleReset = () => {
    const r = { ...DEFAULT_FILTERS };
    setLocal(r);
    if (onReset) {
      onReset();
    } else {
      onApply(r);
    }
  };
  const handleApply = () => { onApply(local); };

  const availableWeapons = local.category ? WEAPONS_BY_CATEGORY[local.category] || [] : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersType')}</label>
        <MiniSelect
          value={local.type}
          options={ITEM_TYPES.map(x => ({ value: x.value, label: t(x.labelKey) }))}
          onChange={(type) => {
            const avail = RARITIES_BY_TYPE[type] || RARITIES_BY_TYPE[''];
            const ok = avail.some(r => r.value === local.rarity);
            setLocal({
              ...local, type,
              wear: type !== 'skin' && type !== '' ? '' : local.wear,
              weapon: type !== 'skin' && type !== '' ? '' : local.weapon,
              category: type !== 'skin' && type !== '' ? '' : local.category,
              rarity: ok ? local.rarity : '',
            });
          }}
        />
      </div>

      {(local.type === '' || local.type === 'skin') && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersWear')}</label>
          <MiniSelect
            value={local.wear}
            options={WEAR_CONDITIONS.map(x => ({ value: x.value, label: t(x.labelKey) }))}
            onChange={(wear) => setLocal({ ...local, wear })}
          />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersRarity')}</label>
        <MiniSelect
          value={local.rarity}
          options={(RARITIES_BY_TYPE[local.type] || RARITIES_BY_TYPE['']).map(r => ({
            value: r.value, label: r.label === 'All' ? t('filtersAllRarities') : r.label,
          }))}
          onChange={(rarity) => setLocal({ ...local, rarity })}
        />
      </div>

      {(local.type === '' || local.type === 'skin') && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersCategory')}</label>
          <MiniSelect
            value={local.category}
            options={CATEGORIES.map(c => ({ value: c.value, label: t(c.labelKey) }))}
            onChange={(category) => setLocal({ ...local, category, weapon: '' })}
          />
        </div>
      )}

      {availableWeapons.length > 0 && (local.type === '' || local.type === 'skin') && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersWeapon')}</label>
          <MiniSelect
            value={local.weapon}
            options={availableWeapons}
            onChange={(weapon) => setLocal({ ...local, weapon })}
          />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersPriceRange')}</label>
        <div className="flex items-center gap-2">
          <MiniInput value={local.minPrice} placeholder="25000" onChange={(minPrice) => setLocal({ ...local, minPrice })} />
          <span className="text-white/30 text-base">—</span>
          <MiniInput value={local.maxPrice} placeholder={t('filtersMaxPrice')} onChange={(maxPrice) => setLocal({ ...local, maxPrice })} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersStatTrak')}</label>
        <Switch checked={local.statTrak} onChange={(v) => setLocal({ ...local, statTrak: v })} />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold uppercase tracking-wider text-white/45">{t('filtersSortBy')}</label>
        <MiniSelect
          value={local.sortBy}
          options={[
            { value: ShopControllerGetShopItemsParams1SortByEnum.VolumeDesc, label: t('filtersSortByVolume') },
            { value: ShopControllerGetShopItemsParams1SortByEnum.PriceAsc, label: t('filtersSortByPriceAsc') },
            { value: ShopControllerGetShopItemsParams1SortByEnum.PriceDesc, label: t('filtersSortByPriceDesc') },
          ]}
          onChange={(sortBy) => setLocal({ ...local, sortBy })}
        />
      </div>

      <div className="flex gap-3 pt-1">
        <Button variant="secondary" onClick={handleReset} stretched size="l">{t('filtersReset')}</Button>
        <Button onClick={handleApply} stretched size="l">{t('filtersApply')}</Button>
      </div>
    </div>
  );
};
